import { useCallback } from "react";
import { useApp } from "../context/AppContext";
import { useSalon } from "../context/SalonContext";
import { API_CONFIG, getApiUrl } from "../config/api";
import {
  ClientGroup,
  ClientGroupMembersResponse,
  ClientGroupPreviewResponse,
  CreateClientGroupRequest,
  MembersMutationResponse,
  RuleTree,
  UpdateClientGroupRequest,
} from "../types/clientGroups";

/**
 * Client Groups API methods. Self-contained hook (mirrors useLoyaltyProgram)
 * rather than growing the 52KB AppContext — every call still routes through
 * `fetchWithAuth`. Scopes to the currently selected business location via
 * `useSalon().businessInfo.id` (multi-location-safe).
 */
export function useClientGroups() {
  const { fetchWithAuth } = useApp();
  const { businessInfo } = useSalon() as any;
  const businessLocationId: string | undefined = businessInfo?.id;

  const listGroups = useCallback(async (): Promise<ClientGroup[]> => {
    if (!businessLocationId) return [];
    const response = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_GROUPS, "", { businessLocationId }),
    );
    return Array.isArray(response?.client_groups) ? response.client_groups : [];
  }, [fetchWithAuth, businessLocationId]);

  const createGroup = useCallback(
    async (data: CreateClientGroupRequest): Promise<ClientGroup> => {
      if (!businessLocationId) throw new Error("No business location selected");
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_GROUPS, "", {
          businessLocationId,
        }),
        { method: "POST", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth, businessLocationId],
  );

  const getGroup = useCallback(
    async (groupId: string): Promise<ClientGroup> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_GROUP_BY_ID, "", { groupId }),
      );
    },
    [fetchWithAuth],
  );

  const updateGroup = useCallback(
    async (
      groupId: string,
      data: UpdateClientGroupRequest,
    ): Promise<ClientGroup> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_GROUP_BY_ID, "", { groupId }),
        { method: "PUT", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth],
  );

  const deleteGroup = useCallback(
    async (groupId: string): Promise<void> => {
      await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_GROUP_BY_ID, "", { groupId }),
        { method: "DELETE" },
      );
    },
    [fetchWithAuth],
  );

  const previewRules = useCallback(
    async (
      rules: RuleTree | null,
      sampleLimit = 25,
    ): Promise<ClientGroupPreviewResponse> => {
      if (!businessLocationId) return { total: 0, sample: [] };
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_GROUPS_PREVIEW, "", {
          businessLocationId,
        }),
        {
          method: "POST",
          body: JSON.stringify({ rules, sample_limit: sampleLimit }),
        },
      );
    },
    [fetchWithAuth, businessLocationId],
  );

  const getMembers = useCallback(
    async (
      groupId: string,
      limit?: number,
      offset = 0,
    ): Promise<ClientGroupMembersResponse> => {
      const query =
        limit !== undefined ? `limit=${limit}&offset=${offset}` : "";
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_GROUP_MEMBERS, query, {
          groupId,
        }),
      );
    },
    [fetchWithAuth],
  );

  const addMembers = useCallback(
    async (
      groupId: string,
      clientIds: string[],
    ): Promise<MembersMutationResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_GROUP_MEMBERS, "", { groupId }),
        { method: "POST", body: JSON.stringify({ client_ids: clientIds }) },
      );
    },
    [fetchWithAuth],
  );

  const removeMembers = useCallback(
    async (
      groupId: string,
      clientIds: string[],
    ): Promise<MembersMutationResponse> => {
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.CLIENT_GROUP_MEMBERS, "", { groupId }),
        { method: "DELETE", body: JSON.stringify({ client_ids: clientIds }) },
      );
    },
    [fetchWithAuth],
  );

  return {
    businessLocationId,
    listGroups,
    createGroup,
    getGroup,
    updateGroup,
    deleteGroup,
    previewRules,
    getMembers,
    addMembers,
    removeMembers,
  };
}

export default useClientGroups;
